// components/portal/SourceBrandPanel.tsx
'use client';

import { activeCampaignResearch, researchingFieldsOf, researchStateOf } from '#lib/leasing/researchPass';
import React, { useCallback, useState } from 'react';
import { BrandMark } from '@/components/ui/BrandMark';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { ChevronDown, Sparkles, Loader2, RefreshCw, ShieldCheck } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { PANEL_BUTTON, PANEL_BUTTON_FONT_SIZE } from './panelButton';
import { PanelSection, PanelRow, type PanelRowTone } from './panelSection';
import { describeEnrichOutcome, describeStoresVerification, formatFinancialValue, formatCnccSector, formatSurface, hostnameOf, type StoresVerification } from '#lib/leasing/enrichNote';
// Same label treatment as the shortlist's own Scoring section, so the two read as one panel.
import { labelHead, ShortlistDoctrine } from './SourceShortlistPanel';
import type { BrandDataCheck, BrandDataFindingSeverity } from '#types/types';
import { readCampaignRow } from '#lib/leasing/campaignRow';
import { isScored } from '#lib/leasing/leadStatus';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';

interface SourceBrandPanelProps {
  /** PortfolioFit currently expanded in the editor (resolves the brand). */
  fitId?: string;
  lang: string;
}

// Minimal shape of the fields we read off the Brand registry entry.
type BrandLike = {
  name?: string;
  enrichmentCheckedAt?: Record<string, string> | null;
  positioningNote?: string | null;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  declaredNetworkSize?: number | null;
  contactIds?: string[];
  contactDiscoveryStatus?: string | null;
  geographicFootprint?: string | null;
  sortableTotalRevenue?: number | null;
  revenueIsEstimate?: boolean | null;
  revenueEntityName?: string | null;
  revenueEntityCountry?: string | null;
  /** The modelled figure, kept apart from the measured one on purpose. See lib/leasing/factGate.ts. */
  estimatedRevenue?: { revenue: number | null; year?: number | null; inputs?: Record<string, unknown> | null } | null;
  /** BODACC court distress. Ungated: the court's word is a fact, not a revenue claim. */
  distressSignal?: { status: string; date: string | null; nature: string | null } | null;
  sortableFinancialScore?: number | null;
  legalEntityCount?: number | null;
  legalEntityIds?: string[];
  surfaceStandard?: number | null;
  surfaceMin?: number | null;
  surfaceMax?: number | null;
  cnccSector?: string | null;
  cnccSubFamily?: string | null;
  cnccActivity?: string | null;
};

// Enrichment value/label/note helpers are shared with the agent's ENRICH_BRAND command ,
// see #lib/leasing/enrichNote (so the panel and the agent log identical chat notes).

// What we hold per field. This is a STATEMENT OF FACT, not an assessment: a field is filled or
// it is not, and the Enrich buttons need to know which. Whether the data is any GOOD is the data
// check's job (the section below), because that takes reading, not a rule.
//  'ok'      = present
//  'missing' = a gap the Enrich action can still fill
//  'none'    = searched / not reachable here (e.g. contacts exhausted, legal outside the
//              French registry), not a fillable gap, so it doesn't count toward the button.
type AspectState = 'ok' | 'missing' | 'none';

// How serious a finding reads, in the bar's existing three shades: amber for something to act
// on, plain for a doubt the check could not settle, grey for a hole it merely noted.
const SEVERITY_TONE: Record<BrandDataFindingSeverity, PanelRowTone> = {
  wrong: 'attention',
  suspect: 'default',
  gap: 'muted',
};

// The state is said by the value's own colour, not by a leading icon: these rows are the same
// rows as the Scoring section above them, and a status column would break that.
const STATE_TONE: Record<AspectState, PanelRowTone> = {
  ok: 'default',
  missing: 'attention',
  none: 'muted',
};

// The court's word, in the reader's language. One map, module level, not inline in a ternary.
const DISTRESS_LABELS: Record<string, { en: string; fr: string }> = {
  LIQUIDATION: { en: 'In liquidation', fr: 'Liquidation judiciaire' },
  REDRESSEMENT: { en: 'In receivership', fr: 'Redressement judiciaire' },
  PLAN: { en: 'Under a court plan', fr: 'Plan en cours' },
  SAUVEGARDE: { en: 'Safeguard procedure', fr: 'Sauvegarde' },
};

// A shortlist in one of these states is already working, so it cannot take a re-run: the pass in
// progress would race it over the same lead. Mirrors the route's own guard.
const ACTIVE_CAMPAIGN_STATUSES = new Set<string>(ACTIVE_SOURCING_CAMPAIGN_STATUSES);

/**
 * Right-bar context panel shown when a brand is selected in the Source editor.
 *
 * THE BAR IS FOR ACTING, NOT RE-READING (owner, 2026-10-03: 'too crowded and presents the
 * scoring two times'). The criteria live in the brand's Scoring tab beside it, so they are not
 * repeated here; the score stays one figure in the header.
 *  - Data       what is still empty, each fillable in a click; the filled fields fold away.
 *  - Data check the verdict: one LLM turn reads the brand's record and says what is actually
 *               wrong with it. It replaced a hand-written present/missing checklist that was
 *               being read as an assessment while it could only ever tick boxes.
 *
 * Fields fill in live via the realtime Brand update the enrich endpoint emits; a progress bar
 * runs on the canvas meanwhile.
 */
export const SourceBrandPanel = ({ fitId, lang }: SourceBrandPanelProps) => {
  const { kernel } = useKernel();
  // Resolve the fit across all three shortlist scopes so the enrich panel works identically for
  // portfolio (PortfolioFit), asset (Lead) and unit (UnitFit) brands, mirrors SourceBulkEnrichPanel.
  const fit = useKernelStore((state) =>
    fitId
      ? ((state.inventory.PortfolioFit.registry[fitId] || state.inventory.Lead.registry[fitId]) as
          {
            brandId?: string;
            brandName?: string | null;
            leadId?: string;
            leadBrandName?: string | null;
            status?: string;
            // Portfolio/unit fits carry `score`, asset rows (Lead) carry `fitScore`.
            score?: number;
            fitScore?: number;
          } | undefined)
      : undefined
  );
  // UnitFit rows carry leadId (not brandId), resolve the brand through the Lead registry.
  const lead = useKernelStore((state) =>
    fit && !fit.brandId && fit.leadId
      ? (state.inventory.Lead.registry[fit.leadId] as { brandId?: string; brandName?: string | null } | undefined)
      : undefined
  );
  const resolvedBrandId = fit?.brandId ?? lead?.brandId;
  // Fields being researched by anyone (the agent, the research pass): they pulse like a click would.
  const kernelEnrichingDims = useKernelStore((state) => (resolvedBrandId ? researchingFieldsOf(state.sourceEnrichingDims[resolvedBrandId], activeCampaignResearch(readCampaignRow(state.inventory, state.sourceSelectedCampaignId)), resolvedBrandId) : undefined));
  const researchState = useKernelStore((state) => researchStateOf(activeCampaignResearch(readCampaignRow(state.inventory, state.sourceSelectedCampaignId)), resolvedBrandId));
  const brand = useKernelStore((state) =>
    resolvedBrandId ? (state.inventory.Brand.registry[resolvedBrandId] as BrandLike | undefined) : undefined
  );
  /** "checked today" / "checked 12 d ago" for a field, from the stamp research writes; unset when never checked. */
  const checkedAgo = (field: string): string | undefined => {
    const at = brand?.enrichmentCheckedAt?.[field];
    if (!at) return undefined;
    // Age is measured against NOW, which is the point of the label; same exemption and reason as
    // SourcingCenterView's staleness check.
    // eslint-disable-next-line react-hooks/purity
    const days = Math.floor((Date.now() - Date.parse(at)) / 86_400_000);
    if (!Number.isFinite(days)) return undefined;
    if (lang === 'fr') return days <= 0 ? "vérifié aujourd'hui" : `vérifié il y a ${days} j`;
    return days <= 0 ? 'checked today' : `checked ${days} d ago`;
  };

  // Re-running the analysis belongs to the ASSET shortlist only: there the focused row IS the Lead,
  // and the Lead names the SourcingCampaign that produced it, which is both the run to re-enter
  // and the thing that says whether a pass is already going. Portfolio/unit rows are produced by
  // other campaign kinds and have no single-brand re-run, so the action simply does not appear.
  const rerunCampaignId = useKernelStore((state) => {
    const leadRow = fitId
      ? (state.inventory.Lead.registry[fitId] as { lastCampaignId?: string | null } | undefined)
      : undefined;
    return typeof leadRow?.lastCampaignId === 'string' ? leadRow.lastCampaignId : '';
  });
  const rerunCampaignStatus = useKernelStore((state) =>
    rerunCampaignId
      ? String(
          readCampaignRow(state.inventory, rerunCampaignId)?.status ?? '',
        ).toUpperCase()
      : '',
  );

  // The last data check for this brand, cached on the brand's runtime overlay. Reading it here
  // rather than re-fetching is what keeps a billed model call from firing on every render, and
  // on every click back onto a row already checked.
  const dataCheck = useKernelStore((state) => {
    if (!resolvedBrandId) return undefined;
    const entry = state.entityKernelState[`Brand:${resolvedBrandId}`]?.state as
      | { runtime?: { dataCheck?: BrandDataCheck } }
      | undefined;
    return entry?.runtime?.dataCheck;
  });

  const [isEnrichingAll, setIsEnrichingAll] = useState(false);
  const [showAllData, setShowAllData] = useState(false);
  const [enrichingDims, setEnrichingDims] = useState<Set<string>>(new Set());
  const [isQueueingRerun, setIsQueueingRerun] = useState(false);
  const [isCheckingData, setIsCheckingData] = useState(false);

  const brandName = brand?.name || fit?.brandName || lead?.brandName || fit?.leadBrandName || (lang === 'fr' ? 'Marque' : 'Brand');
  const logoUrl = brand?.logoUrl || null;
  // Portfolio/unit fits carry `score`, asset rows (Lead) carry `fitScore`. Reading only `score`
  // left the asset shortlist's header with no score at all.
  // A row this run has not scored yet (pending, running, stopped) still carries its PREVIOUS run's
  // score; shown during a research pass it read as a score given before the research (owner,
  // 2026-09-26). Only a scored row shows a score.
  const unscored = typeof fit?.status === 'string' && !isScored(fit.status);
  const fitScore = unscored ? null : typeof fit?.score === 'number' ? fit.score : typeof fit?.fitScore === 'number' ? fit.fitScore : null;


  const present = (v: unknown) => v !== null && v !== undefined && v !== '';
  const contactsCount = brand?.contactIds?.length ?? 0;
  // The claimed store rows. Distinct from declaredNetworkSize ("Network size"), which is what the
  // brand SAYS it operates; this is how many locations we actually hold and can therefore check.
  const storesCount = (brand as { locations?: unknown[] } | undefined)?.locations?.length ?? 0;
  const contactsExhausted = brand?.contactDiscoveryStatus === 'EXHAUSTED';
  const legalCount = (brand?.legalEntityCount ?? brand?.legalEntityIds?.length) ?? 0;
  // Legal/financial registry lookups are French-only; flag non-French brands honestly.
  const footprintHasFr = String(brand?.geographicFootprint ?? '').toUpperCase().split(/[^A-Z]+/).filter(Boolean).includes('FR');
  const financials = brand ? formatFinancialValue(brand, lang) : null;
  const distress = brand?.distressSignal && brand.distressSignal.status !== 'NONE' ? brand.distressSignal : null;
  const distressLabel = distress
    ? `${(DISTRESS_LABELS[distress.status] ?? { en: distress.status, fr: distress.status })[lang === 'fr' ? 'fr' : 'en']}${distress.date ? ` · ${String(distress.date).slice(0, 4)}` : ''}`
    : null;
  const missingLabel = lang === 'fr' ? 'Manquant' : 'Missing';

  type Aspect = { key: string; label: string; value: React.ReactNode; state: AspectState };
  const aspects: Aspect[] = [
    {
      key: 'website',
      label: lang === 'fr' ? 'Site web' : 'Website',
      value: brand?.websiteUrl ? (
        <a
          href={brand.websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="hover:text-white hover:underline"
        >
          {hostnameOf(brand.websiteUrl)}
        </a>
      ) : (
        missingLabel
      ),
      state: present(brand?.websiteUrl) ? 'ok' : 'missing',
    },
    {
      key: 'network',
      label: lang === 'fr' ? 'Taille de réseau' : 'Network size',
      value: typeof brand?.declaredNetworkSize === 'number'
        ? `${brand.declaredNetworkSize} ${lang === 'fr' ? 'points de vente' : 'stores'}`
        : missingLabel,
      state: typeof brand?.declaredNetworkSize === 'number' ? 'ok' : 'missing',
    },
    {
      key: 'positioning',
      label: lang === 'fr' ? 'Positionnement' : 'Positioning',
      value: brand?.positioningNote || missingLabel,
      state: brand?.positioningNote ? 'ok' : 'missing',
    },
    {
      key: 'category',
      label: lang === 'fr' ? 'Catégorie (CNCC)' : 'Category (CNCC)',
      value: (brand?.cnccActivity ? `${brand.cnccActivity} (${(brand.cnccSector ?? '').toUpperCase()})` : formatCnccSector(brand?.cnccSector)) ?? missingLabel,
      state: present(brand?.cnccSector) ? 'ok' : 'missing',
    },
    {
      key: 'surface',
      label: lang === 'fr' ? 'Surface' : 'Floor area',
      value: (brand && formatSurface(brand)) || missingLabel,
      state: brand && formatSurface(brand) ? 'ok' : 'missing',
    },
    {
      key: 'logo',
      label: 'Logo',
      value: present(brand?.logoUrl) ? (lang === 'fr' ? 'Présent' : 'Present') : missingLabel,
      state: present(brand?.logoUrl) ? 'ok' : 'missing',
    },
    {
      // Enriching this one does NOT fetch more stores: it sends every claimed store to the
      // existing per-store reviewer, which checks each address against the live web and corrects
      // or removes the ones that are not real. Some of these rows were invented by a model
      // (market-sourcing's Archivist writes LLM-authored addresses straight to BrandLocation), so
      // "43 stores" is a claim, not a fact, until they are checked.
      key: 'stores',
      label: lang === 'fr' ? 'Magasins' : 'Stores',
      value:
        storesCount > 0
          ? `${storesCount} ${lang === 'fr' ? (storesCount > 1 ? 'magasins' : 'magasin') : storesCount > 1 ? 'stores' : 'store'}`
          : missingLabel,
      state: storesCount > 0 ? 'ok' : 'missing',
    },
    {
      key: 'contacts',
      label: 'Contacts',
      value: contactsCount > 0
        ? `${contactsCount} ${lang === 'fr' ? 'trouvés' : 'found'}`
        : contactsExhausted
          ? (lang === 'fr' ? 'Aucun trouvé' : 'None found')
          : missingLabel,
      state: contactsCount > 0 ? 'ok' : contactsExhausted ? 'none' : 'missing',
    },
    {
      key: 'financials',
      label: lang === 'fr' ? 'Finances' : 'Financials',
      // Only a VERIFIED figure reaches `financials` (the mapper nulls the column otherwise), so
      // this row is now empty for most brands and that is the honest state. The score is derived
      // from the same filings, so it cannot survive them.
      value: financials
        ? financials
        : present(brand?.sortableFinancialScore)
          ? `Score ${brand?.sortableFinancialScore}`
          : missingLabel,
      state: financials || present(brand?.sortableFinancialScore) ? 'ok' : 'missing',
    },
    // ONLY WHEN THERE IS NO PROVED FIGURE. Showing both puts "€94M" and "Estimate €1.1B" on the
    // same panel for the same brand - and on Skechers that is exactly what happened, the model
    // being eleven times the filed accounts. A reader offered two revenues remembers the bigger
    // one. The model is a fallback for when we have nothing, not a second opinion.
    //
    // THE ESTIMATE, ON ITS OWN ROW, IN WORDS. It is store count x typical surface x a sector
    // benchmark; no source states it. It is kept because for the ~45% of French companies that
    // file confidentially it is the only signal there is, and it is separated because a modelled
    // number sharing a row and a format with a filed one is how both come to be trusted equally.
    // Its state is never 'ok': an estimate is not a filled-in field, and the Enrich badge above
    // must keep counting this brand as still needing a real figure.
    // The blurred "Estimation" row is HIDDEN (owner's call, 2026-09-19), not deleted: the
    // estimate still feeds scoring, it just no longer occupies a row saying "later". See the
    // matching removal on the Revenus tab (BrandRevenueChart).
    {
      key: 'legal',
      label: lang === 'fr' ? 'Entités légales' : 'Legal entities',
      // THE COURT'S WORD OUTRANKS THE COUNT. A brand whose company is in liquidation showing a
      // serene "1 entity" is the panel hiding the single most decision-relevant fact it holds.
      // BODACC-cited, ungated: a liquidated tenant with a verified revenue shows both.
      value: distressLabel
        ? (
            <span className="text-amber-300/90" title={distress?.nature ?? undefined}>
              {distressLabel}
            </span>
          )
        : legalCount > 0
          ? `${legalCount} ${legalCount > 1 ? (lang === 'fr' ? 'entités' : 'entities') : (lang === 'fr' ? 'entité' : 'entity')}`
          : footprintHasFr
            ? missingLabel
            : (lang === 'fr' ? 'Hors registre FR' : 'Outside the FR registry'),
      state: distress ? 'missing' : legalCount > 0 ? 'ok' : footprintHasFr ? 'missing' : 'none',
    },
  ];

  // A check written in another language is not shown: the user reported a French/English mix on
  // screen, and half-translated findings are exactly that. Re-running rewrites them in the
  // language currently on screen.
  const check = dataCheck && dataCheck.lang === lang ? dataCheck : undefined;

  // The verification outcome, in the user's words rather than the verifier's. Every reason here is
  // a real state the register can put us in, and they mean genuinely different things:
  // 'confidential' is permanent and correct, 'no-anchor' means no company of that name trades
  // where this brand trades, and 'stale' means the company exists but stopped publishing.
  const verdict = ((): { text: string; detail: string; tone: PanelRowTone } | null => {
    const v = check?.verification;
    if (!v) return null;
    const fr = lang === 'fr';
    if (v.revenue.outcome === 'VERIFIED') {
      return {
        text: fr ? `Vérifié · ${v.revenue.year}` : `Verified · ${v.revenue.year}`,
        detail: fr
          ? `Chiffre d'affaires ${v.revenue.year} de ${v.anchor.entityName}, publié au registre officiel. Société identifiée par recoupement avec les villes où la marque a réellement des magasins.`
          : `${v.revenue.year} revenue for ${v.anchor.entityName}, filed with the official register. The company was identified by cross-checking the towns where the brand actually has shops.`,
        tone: 'default',
      };
    }
    const reason = v.revenue.reason ?? v.anchor.reason;
    const said: Record<string, { en: string; fr: string; detailEn: string; detailFr: string }> = {
      confidential: {
        en: 'No public accounts', fr: 'Comptes confidentiels',
        detailEn: `${v.anchor.entityName ?? 'The company'} files its accounts confidentially, which is legal and permanent. No figure exists to find.`,
        detailFr: `${v.anchor.entityName ?? 'La société'} dépose ses comptes de façon confidentielle, ce qui est légal et définitif. Aucun chiffre n'existe.`,
      },
      stale: {
        en: 'Accounts too old', fr: 'Comptes trop anciens',
        detailEn: `The last published accounts are from ${v.revenue.year}, too old to describe this business today.`,
        detailFr: `Les derniers comptes publiés datent de ${v.revenue.year}, trop anciens pour décrire l'activité actuelle.`,
      },
      'no-anchor': {
        en: 'Company not identified', fr: 'Société non identifiée',
        detailEn: 'No company of this name is registered in the towns where this brand has shops, so none of them could be proved to be it. Often a franchise network filed under names of its own.',
        detailFr: "Aucune société de ce nom n'est immatriculée dans les villes où la marque a des magasins. Souvent un réseau de franchise déposé sous d'autres noms.",
      },
      'not-fetched': {
        en: 'Accounts not read yet', fr: 'Comptes pas encore lus',
        detailEn: `${v.anchor.entityName ?? 'This company'} is too large to file confidentially, so its accounts are public by law - we simply do not read the register that holds them yet. This is our gap, not the brand's privacy.`,
        detailFr: `${v.anchor.entityName ?? 'Cette société'} est trop grande pour déposer ses comptes confidentiellement : ils sont publics par obligation. Nous ne lisons pas encore le registre qui les contient. C'est notre manque, pas la confidentialité de la marque.`,
      },
      'no-registry': {
        en: 'Outside the FR registry', fr: 'Hors registre FR',
        detailEn: 'This brand has no French store footprint, so the French register cannot confirm anything about it.',
        detailFr: "Cette marque n'a pas de magasins en France, le registre français ne peut donc rien confirmer.",
      },
      'no-provider': {
        en: 'Register unreachable', fr: 'Registre injoignable',
        detailEn: 'The official register did not answer. This is our problem, not a fact about the brand: try again.',
        detailFr: "Le registre officiel n'a pas répondu. C'est notre problème, pas un fait sur la marque : réessayez.",
      },
    };
    const m = said[reason ?? ''] ?? {
      en: 'Not verified', fr: 'Non vérifié',
      detailEn: 'Nothing could be proved for this brand on this pass.',
      detailFr: "Rien n'a pu être prouvé pour cette marque lors de ce passage.",
    };
    return {
      text: fr ? m.fr : m.en,
      detail: fr ? m.detailFr : m.detailEn,
      // 'confidential' and 'no-registry' are permanent facts about the world, not gaps in our work,
      // so they read muted rather than as something to go and fix.
      tone: reason === 'confidential' || reason === 'no-registry' ? 'muted' : 'attention',
    };
  })();

  // Only true, still-fillable gaps drive the button count ('none' = searched / unreachable).
  const gapsCount = aspects.filter((a) => a.state === 'missing').length;
  const filledCount = aspects.filter((a) => a.state === 'ok').length;
  const shownAspects = showAllData ? aspects : aspects.filter((a) => a.state === 'missing');
  const canEnrich = Boolean(resolvedBrandId);

  // A billed pass: one deliberate click, never a side effect. It is refused while the shortlist is
  // already working, and the click is swallowed for as long as this one is being queued.
  const canRerun = Boolean(rerunCampaignId && resolvedBrandId);
  const rerunRunning = ACTIVE_CAMPAIGN_STATUSES.has(rerunCampaignStatus);

  const handleRerun = useCallback(async () => {
    const k = kernel.current;
    const brandId = resolvedBrandId;
    if (!k || !brandId || !rerunCampaignId || rerunRunning || isQueueingRerun) return;
    setIsQueueingRerun(true);
    try {
      await k.rerunSourcingCampaignBrand(rerunCampaignId, brandId);
      toast.success(
        lang === 'fr'
          ? `Nouvelle analyse lancée pour ${brandName}.`
          : `New analysis started for ${brandName}.`,
      );
    } catch (error: any) {
      console.error('[SourceBrandPanel] rerun analysis failed', error);
      toast.error(
        error?.message || (lang === 'fr' ? "Échec du lancement de l'analyse." : 'Failed to start the analysis.'),
      );
    } finally {
      setIsQueueingRerun(false);
    }
  }, [kernel, resolvedBrandId, rerunCampaignId, rerunRunning, isQueueingRerun, lang, brandName]);

  // A billed LLM turn, so it only ever runs from this click: never on mount, never on focus.
  const handleCheckData = useCallback(async () => {
    const k = kernel.current;
    const brandId = resolvedBrandId;
    if (!k || !brandId || isCheckingData) return;
    setIsCheckingData(true);
    try {
      await k.checkBrandData(brandId, lang);
    } catch (error: any) {
      console.error('[SourceBrandPanel] data check failed', error);
      toast.error(error?.message || (lang === 'fr' ? 'Échec du contrôle des données.' : 'Data check failed.'));
    } finally {
      setIsCheckingData(false);
    }
  }, [kernel, resolvedBrandId, isCheckingData, lang]);

  const handleEnrichDimension = useCallback(async (dim: string) => {
    const k = kernel.current;
    const brandId = resolvedBrandId;
    if (!k || !brandId || isEnrichingAll || enrichingDims.has(dim)) return;
    const before = brand;
    setEnrichingDims((prev) => new Set(prev).add(dim));
    try {
      const result = await k.enrichBrand(brandId, dim);
      const after = (result && (result as { brand?: BrandLike }).brand) || undefined;
      // `stores` verifies live and may DELETE rows, so it reports what it actually did rather
      // than a field it filled. The note carries the same numbers as the toast below.
      const stores = (result as { stores?: StoresVerification } | undefined)?.stores;
      const storesTotal = (result as { storesTotal?: number } | undefined)?.storesTotal;
      // A 200 with nothing filled is not success. Say which it was.
      if (dim === 'stores') {
        const verified = describeStoresVerification(stores, storesTotal, lang);
        if (verified.ok) toast.success(verified.message);
        else toast.error(verified.message);
        return;
      }
      const outcome = describeEnrichOutcome(before, after, dim, lang);
      if (outcome.ok) toast.success(outcome.message);
      else toast.error(outcome.message);
    } catch (error: any) {
      console.error('[SourceBrandPanel] enrich dimension failed', dim, error);
      toast.error(error?.message || (lang === 'fr' ? "Échec de l'enrichissement." : 'Enrichment failed.'));
    } finally {
      setEnrichingDims((prev) => {
        const next = new Set(prev);
        next.delete(dim);
        return next;
      });
    }
  }, [kernel, resolvedBrandId, isEnrichingAll, enrichingDims, lang, brand, brandName]);

  const handleEnrichAll = useCallback(async () => {
    const k = kernel.current;
    const brandId = resolvedBrandId;
    if (!k || !brandId || isEnrichingAll) return;
    const before = brand;
    setIsEnrichingAll(true);
    try {
      const result = await k.enrichBrand(brandId);
      const after = (result && (result as { brand?: BrandLike }).brand) || undefined;
      toast.success(lang === 'fr' ? 'Marque enrichie.' : 'Brand enriched.');
    } catch (error: any) {
      console.error('[SourceBrandPanel] enrich all failed', error);
      toast.error(error?.message || (lang === 'fr' ? "Échec de l'enrichissement." : 'Enrichment failed.'));
    } finally {
      setIsEnrichingAll(false);
    }
  }, [kernel, resolvedBrandId, isEnrichingAll, lang, brand, brandName]);

  if (researchState === 'waiting') {
    return (
      <div className="px-4 py-6 text-center text-white/40" style={{ fontSize: 12 }}>
        {lang === 'fr' ? "En attente de recherche. Les données de cette marque apparaissent une fois reconstruites par l'agent." : "Waiting for research. This brand's data appears once the agent has rebuilt it."}
      </div>
    );
  }

  return (
    <div className="custom-scrollbar flex h-full w-full flex-col overflow-y-auto px-3 py-3">
      <div className="flex items-center gap-3 px-1">
        <BrandMark logoUrl={logoUrl} name={brandName} size={36} radius={8} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>{brandName}</div>
          <div className="truncate text-[#8E8E8E]" style={{ fontSize: 12 }}>
            {fitScore !== null ? `Score ${fitScore}` : null}
          </div>
        </div>
      </div>

      {/* Data: what is still empty, each fillable in a click. The filled fields fold away behind
          'All fields', where each keeps its own refresh. */}
      <PanelSection title={lang === 'fr' ? 'Données' : 'Data'} aside={`${filledCount}/${aspects.length}`}>
        {shownAspects.length === 0 ? (
          <div className="mt-1.5 text-[#8E8E8E]" style={{ fontSize: 13 }}>
            {lang === 'fr' ? 'Fiche complète.' : 'Record complete.'}
          </div>
        ) : (
          <dl className="mt-1 flex flex-col">
            {shownAspects.map((aspect) => {
              const busy = enrichingDims.has(aspect.key) || isEnrichingAll || Boolean(kernelEnrichingDims?.includes(aspect.key));
              return (
                <PanelRow
                  key={aspect.key}
                  label={aspect.label}
                  labelTitle={checkedAgo(aspect.key)}
                  checked={showAllData ? checkedAgo(aspect.key) : undefined}
                  value={aspect.value}
                  tone={STATE_TONE[aspect.state]}
                  pulsing={busy}
                  action={
                    <button
                      type="button"
                      title={lang === 'fr' ? `Enrichir : ${aspect.label}` : `Enrich ${aspect.label}`}
                      onClick={() => handleEnrichDimension(aspect.key)}
                      disabled={!canEnrich || busy || isEnrichingAll}
                      className={'transition-colors ' + (busy ? 'text-[#B5B5B5]' : 'text-[#6E6E6E] hover:text-[#E8E8E8] disabled:opacity-40')}
                    >
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    </button>
                  }
                />
              );
            })}
          </dl>
        )}
        <button
          type="button"
          onClick={() => setShowAllData((open) => !open)}
          aria-expanded={showAllData}
          className="mt-1 flex items-center gap-1 text-[#8E8E8E] transition-colors hover:text-[#ECECEC]"
          style={{ fontSize: 12 }}
        >
          {showAllData ? (lang === 'fr' ? 'Masquer les champs remplis' : 'Hide filled fields') : (lang === 'fr' ? 'Tous les champs' : 'All fields')}
          <ChevronDown size={13} className={`transition-transform ${showAllData ? 'rotate-180' : ''}`} />
        </button>
        {gapsCount > 0 ? (
          <button
            type="button"
            onClick={handleEnrichAll}
            disabled={!canEnrich}
            className={`mt-3 ${PANEL_BUTTON}`}
            style={{ fontSize: PANEL_BUTTON_FONT_SIZE }}
          >
            {isEnrichingAll ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {lang === 'fr' ? 'Enrichissement…' : 'Enriching…'}
              </>
            ) : (
              <>
                <Sparkles size={14} />
                {lang === 'fr' ? `Compléter (${gapsCount})` : `Fill the gaps (${gapsCount})`}
              </>
            )}
          </button>
        ) : null}
      </PanelSection>

      {/* Data check.      {/* Data check. What the rows above cannot say: a model reads the brand's actual record and
          reports what contradicts what, what the web disproves, what is too old to act on. It is
          a real call, so it fires from this button only and its verdict is kept on the brand. */}
      {verdict || check ? (
        <PanelSection
          title={lang === 'fr' ? 'Contrôle des données' : 'Data check'}
          aside={check ? check.findings.length : null}
        >
          {/* WHAT THE CLICK PROVED, before what it thinks. The check now tries to earn the record
              back - which company this brand is, and what that company filed - so the outcome is
              stated first. A failure is the useful half: "confidential" means the accounts are
              legally private and no amount of clicking will ever produce them, and saying so is
              what stops the user paying to search again. */}
          {verdict ? (
            <PanelRow
              label={labelHead(lang === 'fr' ? 'Vérification' : 'Verification')}
              labelTitle={verdict.detail}
              tone={verdict.tone}
              value={<span title={verdict.detail} style={{ fontSize: 12.5 }}>{verdict.text}</span>}
            />
          ) : null}
          {check ? (
            check.findings.length === 0 ? (
              <div className="mt-2 text-[#6E6E6E]" style={{ fontSize: 11 }}>
                {lang === 'fr' ? 'Rien à signaler sur cette fiche.' : 'Nothing to flag on this record.'}
              </div>
            ) : (
              <dl className="mt-1.5 flex flex-col">
                {check.findings.map((finding, index) => (
                  <PanelRow
                    key={`${finding.label}-${index}`}
                    label={labelHead(finding.label)}
                    labelTitle={finding.detail}
                    tone={SEVERITY_TONE[finding.severity]}
                    // The bar is too narrow to read prose in (same reason the Scoring section
                    // keeps its rationale out), so the row states the verdict and carries the
                    // full sentence as its own tooltip.
                    // Pinned: globals.css forces every span back to 12px, and an unpinned one
                    // here would sit half a pixel off every other value in the bar.
                    value={<span title={finding.detail} style={{ fontSize: 12.5 }}>{finding.verdict}</span>}
                  />
                ))}
              </dl>
            )
          ) : null}
        </PanelSection>
      ) : null}

      {/* The two paid re-reads, side by side and quiet: re-score this brand from scratch, or have
          a model read its record for what is wrong. */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleRerun}
          disabled={!canRerun || rerunRunning || isQueueingRerun}
          title={lang === 'fr' ? 'Rechercher et noter cette marque à nouveau' : 'Research and score this brand again'}
          className="flex h-8 items-center justify-center gap-1.5 rounded-full border border-[#262626] px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.08] disabled:opacity-40 disabled:hover:bg-transparent"
          style={{ fontSize: 12 }}
        >
          {rerunRunning || isQueueingRerun ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {rerunRunning || isQueueingRerun ? (lang === 'fr' ? 'Analyse…' : 'Scoring…') : (lang === 'fr' ? 'Renoter' : 'Score again')}
        </button>
        <button
          type="button"
          onClick={handleCheckData}
          disabled={!canEnrich || isCheckingData}
          title={lang === 'fr' ? 'Faire relire la fiche par un modèle' : 'Have a model read the record for errors'}
          className="flex h-8 items-center justify-center gap-1.5 rounded-full border border-[#262626] px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.08] disabled:opacity-40 disabled:hover:bg-transparent"
          style={{ fontSize: 12 }}
        >
          {isCheckingData ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
          {isCheckingData ? (lang === 'fr' ? 'Contrôle…' : 'Checking…') : check ? (lang === 'fr' ? 'Recontrôler' : 'Check again') : (lang === 'fr' ? 'Contrôler' : 'Check data')}
        </button>
      </div>

      {/* The doctrine stays reachable with a brand open (SRC-003): the same entry the shortlist
          bar carries, scoped to the campaign this lead belongs to. Asset scope only, like the
          shortlist bar: portfolio rows have no rubric or brief to show. */}
      {rerunCampaignId ? (
        <ShortlistDoctrine
          key={rerunCampaignId}
          campaignId={rerunCampaignId}
          scope="asset"
          lang={lang}
          stillRunning={false}
          doctrineOnly
        />
      ) : null}
    </div>
  );
};

SourceBrandPanel.displayName = 'SourceBrandPanel';
